import { useEffect, useRef, useState, useCallback } from 'react';
import { Audio, InterruptionModeIOS, InterruptionModeAndroid } from 'expo-av';
import { WS_BASE_URL } from '@/constants/Configs';
import { AUDIO_CONFIG, createWavFromUlawBytes, decodeBase64ToBytes } from './audioUtils';

export type AudioStreamMessage = {
  type: 'audio' | 'call_status' | 'connected';
  sessionId?: string;
  timestamp?: string;
  audio?: string;
  speaker?: 'ai' | 'merchant';
  status?: 'started' | 'ended' | 'error';
  message?: string;
};

type CallStatus = 'started' | 'ended' | 'error';

interface UseAudioStreamWebSocketProps {
  sessionId: string | null;
  enabled?: boolean;
  onCallStatus?: (status: CallStatus, message?: string) => void;
  onConnectionChange?: (connected: boolean) => void;
  onError?: (error: Event) => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;

export const useAudioStreamWebSocket = ({
  sessionId,
  enabled = true,
  onCallStatus,
  onConnectionChange,
  onError,
}: UseAudioStreamWebSocketProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [callStatus, setCallStatus] = useState<CallStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // WebSocket refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const shouldReconnectRef = useRef(true);

  // Audio buffering refs
  const audioBufferRef = useRef<number[]>([]);
  const bufferTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wavQueueRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);
  const soundRef = useRef<Audio.Sound | null>(null);
  const wavChunkCountRef = useRef(0);
  const hasStartedPlaybackRef = useRef(false);

  // Callback refs to avoid dependency issues
  const callbackRefs = useRef({ onCallStatus, onConnectionChange, onError });
  useEffect(() => {
    callbackRefs.current = { onCallStatus, onConnectionChange, onError };
  }, [onCallStatus, onConnectionChange, onError]);

  const initAudio = useCallback(async () => {
    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        staysActiveInBackground: true,
        interruptionModeIOS: InterruptionModeIOS.DuckOthers,
        playsInSilentModeIOS: true,
        shouldDuckAndroid: true,
        interruptionModeAndroid: InterruptionModeAndroid.DuckOthers,
        playThroughEarpieceAndroid: false,
      });
    } catch (err) {
      console.error('[AudioStream] Failed to set audio mode:', err);
    }
  }, []);

  const playNextWav = useCallback(async () => {
    if (isPlayingRef.current || wavQueueRef.current.length === 0) return;

    isPlayingRef.current = true;
    setIsPlaying(true);

    try {
      const wavDataUrl = wavQueueRef.current.shift();
      if (!wavDataUrl) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        return;
      }

      if (soundRef.current) {
        try {
          await soundRef.current.unloadAsync();
        } catch {
          // Ignore unload errors
        }
        soundRef.current = null;
      }

      const { sound } = await Audio.Sound.createAsync(
        { uri: wavDataUrl },
        { shouldPlay: true },
        (status) => {
          if (status.isLoaded && status.didJustFinish) {
            isPlayingRef.current = false;
            setIsPlaying(false);
            playNextWav();
          }
        }
      );

      soundRef.current = sound;
    } catch (err) {
      console.error('[AudioStream] Failed to play WAV:', err);
      isPlayingRef.current = false;
      setIsPlaying(false);
      playNextWav();
    }
  }, []);

  const flushAudioBuffer = useCallback(() => {
    if (audioBufferRef.current.length === 0) return;

    const ulawBytes = new Uint8Array(audioBufferRef.current);
    audioBufferRef.current = [];

    if (bufferTimerRef.current) {
      clearTimeout(bufferTimerRef.current);
      bufferTimerRef.current = null;
    }

    const wavDataUrl = createWavFromUlawBytes(ulawBytes);
    wavQueueRef.current.push(wavDataUrl);
    wavChunkCountRef.current++;

    // Start playback after pre-buffering is complete
    if (!hasStartedPlaybackRef.current && wavChunkCountRef.current >= AUDIO_CONFIG.PRE_BUFFER_CHUNKS) {
      hasStartedPlaybackRef.current = true;
      playNextWav();
    } else if (hasStartedPlaybackRef.current) {
      playNextWav();
    }
  }, [playNextWav]);

  const addAudioToBuffer = useCallback((base64Audio: string) => {
    const bytes = decodeBase64ToBytes(base64Audio);
    audioBufferRef.current.push(...bytes);

    if (bufferTimerRef.current) {
      clearTimeout(bufferTimerRef.current);
    }

    if (audioBufferRef.current.length >= AUDIO_CONFIG.BUFFER_SIZE) {
      flushAudioBuffer();
    } else {
      bufferTimerRef.current = setTimeout(flushAudioBuffer, AUDIO_CONFIG.BUFFER_TIMEOUT_MS);
    }
  }, [flushAudioBuffer]);

  const clearAudioState = useCallback(() => {
    audioBufferRef.current = [];
    wavQueueRef.current = [];
    wavChunkCountRef.current = 0;
    hasStartedPlaybackRef.current = false;
    isPlayingRef.current = false;
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: AudioStreamMessage = JSON.parse(event.data);

      switch (message.type) {
        case 'connected':
          console.log('[AudioStream] Server confirmed connection:', message.sessionId);
          break;

        case 'audio':
          if (message.audio) {
            addAudioToBuffer(message.audio);
          }
          break;

        case 'call_status':
          if (message.status) {
            setCallStatus(message.status);
            callbackRefs.current.onCallStatus?.(message.status, message.message);

            if (message.status === 'ended' || message.status === 'error') {
              flushAudioBuffer();
              clearAudioState();
            }
          }
          break;
      }
    } catch (parseError) {
      console.error('[AudioStream] Failed to parse message:', parseError);
      setError('Failed to parse message');
    }
  }, [addAudioToBuffer, flushAudioBuffer, clearAudioState]);

  const connect = useCallback((targetSessionId: string) => {
    if (wsRef.current?.readyState === WebSocket.CONNECTING ||
        wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(`${WS_BASE_URL}/mobile-audio?sessionId=${targetSessionId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        callbackRefs.current.onConnectionChange?.(true);
        initAudio();
      };

      ws.onmessage = handleMessage;

      ws.onclose = (event) => {
        setIsConnected(false);
        callbackRefs.current.onConnectionChange?.(false);
        wsRef.current = null;

        // Only reconnect if: should reconnect, abnormal close, not exceeded attempts
        if (
          shouldReconnectRef.current &&
          event.code !== 1000 &&
          reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS
        ) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect(targetSessionId);
          }, delay);
        }
      };

      ws.onerror = (event) => {
        console.error('[AudioStream] WebSocket error:', event);
        setError('WebSocket connection error');
        callbackRefs.current.onError?.(event);
      };
    } catch (connectError) {
      console.error('[AudioStream] Failed to create connection:', connectError);
      setError('Failed to connect');
    }
  }, [initAudio, handleMessage]);

  const disconnect = useCallback(async () => {
    // Prevent any pending reconnection attempts
    shouldReconnectRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (bufferTimerRef.current) {
      clearTimeout(bufferTimerRef.current);
      bufferTimerRef.current = null;
    }

    if (soundRef.current) {
      try {
        await soundRef.current.stopAsync();
        await soundRef.current.unloadAsync();
      } catch {
        // Ignore cleanup errors
      }
      soundRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounted');
      wsRef.current = null;
    }

    clearAudioState();
    setIsConnected(false);
    setCallStatus(null);
    setError(null);
    setIsPlaying(false);
    reconnectAttempts.current = 0;
  }, [clearAudioState]);

  useEffect(() => {
    shouldReconnectRef.current = true;

    if (sessionId && enabled) {
      connect(sessionId);
    }
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, enabled]);

  const reconnect = useCallback(() => {
    if (sessionId) {
      disconnect();
      setTimeout(() => connect(sessionId), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return {
    isConnected,
    callStatus,
    error,
    isPlaying,
    disconnect,
    reconnect,
  };
};
